import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "../axiosConfig";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

const Header = () => (
  <header className="sticky top-0 z-50 border-b border-[#E8EAE5] bg-white/90 backdrop-blur-xl">
    <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 md:px-6">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img
            src="/logo_and_name.png"
            alt="KHORN AI"
            className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none"
          />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 text-[13px] text-[#5F665E] md:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <a
        href="https://app.khorn-ai.com/plan"
        className="rounded-full bg-[#15130F] px-5 py-2.5 text-[13px] font-medium text-[#FAFAF7] transition hover:bg-[#1F2A22]"
      >
        เริ่มใช้งาน
      </a>
    </div>
  </header>
);

const formatCount = (count, fallback) => {
  if (!count) return fallback;
  return `${Math.floor(count / 100) * 100}+`;
};

const McpPage = () => {
  const [lawCount, setLawCount] = useState(null);

  useEffect(() => {
    let active = true;

    const loadLawCount = async () => {
      try {
        const response = await axios.get("/th-law-titles");
        if (active) setLawCount((response.data || []).length);
      } catch (error) {
        if (active) setLawCount(null);
      }
    };

    loadLawCount();
    return () => {
      active = false;
    };
  }, []);

  const stats = [
    ["กฎหมายไทย", formatCount(lawCount, "1,100+")],
    ["ฎีกา", "100,000+"],
  ];

  return (
    <main className="min-h-screen bg-white text-[#15130F]">
      <Helmet>
        <title>KHORN MCP | ต่อฐานกฎหมายไทยกับ Codex และ Claude</title>
        <meta
          name="description"
          content="KHORN MCP เชื่อมฐานกฎหมายไทยและฎีกาเข้ากับ AI workspace เช่น Codex และ Claude เพื่อให้ทีมค้นแหล่งอ้างอิงกฎหมายได้จากเครื่องมือที่ใช้อยู่"
        />
        <meta
          name="keywords"
          content="KHORN MCP, legal MCP, Thai law MCP, Codex law search, Claude law search, ค้นกฎหมาย, ค้นฎีกา"
        />
        <meta property="og:title" content="KHORN MCP | ฐานกฎหมายไทยสำหรับ AI workspace" />
        <meta
          property="og:description"
          content="ต่อฐานกฎหมายไทยและฎีกาเข้ากับ Codex, Claude และเครื่องมือที่รองรับ MCP"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.khorn-ai.com/mcp" />
        <link rel="canonical" href="https://www.khorn-ai.com/mcp" />
      </Helmet>

      <Header />

      <section className="relative overflow-hidden px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto grid max-w-[1180px] items-center gap-12 lg:grid-cols-[0.92fr_1.08fr]">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-[#3777E3]">
              KHORN MCP
            </p>
            <h1 className="mt-6 max-w-3xl text-6xl font-semibold leading-[0.95] tracking-[-0.07em] md:text-8xl">
              ต่อฐานกฎหมายกับ AI ของคุณ
            </h1>
            <p className="mt-7 text-2xl font-semibold tracking-[-0.04em] text-[#5F665E]">
              KHORN AI MCP
            </p>
            <a
              href="https://app.khorn-ai.com/plan"
              className="mt-9 inline-flex h-14 items-center justify-center rounded-xl bg-[#15130F] px-8 text-base font-semibold text-white shadow-[0_8px_18px_rgba(0,0,0,0.16)] transition hover:bg-[#1F2A22]"
            >
              เริ่มใช้งาน
            </a>
          </div>

          <div className="relative min-h-[420px]">
            <div className="absolute left-1/2 top-[106px] h-[150px] w-px -translate-x-1/2 bg-[#DDE3D8]" />
            <div className="absolute left-[24%] top-[106px] h-px w-[52%] bg-[#DDE3D8]" />

            <div className="relative grid grid-cols-2 gap-10 pt-10">
              <McpLogo src="/images/codex-logo.png" />
              <McpLogo src="/images/claude-logo.svg" />
            </div>

            <McpLogo src="/logo.png" className="relative mx-auto mt-24" />
          </div>
        </div>
      </section>

      <section className="px-5 pb-12 md:px-10">
        <div className="mx-auto grid max-w-[900px] gap-8 border-y border-[#E8EAE5] py-12 md:grid-cols-2">
          {stats.map(([label, value], index) => (
            <div
              key={label}
              className={`text-center ${index > 0 ? "md:border-l md:border-[#E8EAE5]" : ""}`}
            >
              <div
                className={`text-6xl font-semibold tracking-[-0.06em] md:text-7xl ${
                  index === 0 ? "text-[#3777E3]" : "text-[#18A64A]"
                }`}
              >
                {value}
              </div>
              <p className="mt-3 text-base font-semibold text-[#6A7168]">
                {label} ที่ครอบคลุม
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 py-16 md:px-10 md:py-24">
        <div className="mx-auto max-w-[980px]">
          <div className="mb-12 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-[#6A7168]">
              วิธีเชื่อมต่อ
            </p>
            <h2 className="mt-5 text-5xl font-semibold leading-[0.98] tracking-[-0.06em] md:text-7xl">
              เชื่อมต่ออย่างไร
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              ["01", "เพิ่ม KHORN MCP", "นำจุดเชื่อมต่อ MCP ไปใส่ใน Codex, Claude หรือเครื่องมือที่รองรับ MCP"],
              ["02", "เลือกเครื่องมือค้น", "เปิดใช้ค้นกฎหมายและค้นฎีกาจากฐานข้อมูล KHORN"],
              ["03", "เริ่มเรียกแหล่งอ้างอิง", "ให้พื้นที่ทำงาน AI เรียกกฎหมายและฎีกาเป็นชั้นข้อมูลอ้างอิง"],
            ].map(([num, title, text]) => (
              <article key={num} className="rounded-[28px] bg-[#FAFAF7] p-7">
                <p className="text-sm font-semibold text-[#7C8579]">{num}</p>
                <h3 className="mt-8 text-2xl font-semibold tracking-[-0.04em]">
                  {title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-[#5F665E]">{text}</p>
              </article>
            ))}
          </div>

          <div className="mt-14 text-center">
            <a
              href="https://app.khorn-ai.com/plan"
              className="inline-flex h-14 items-center justify-center rounded-xl bg-[#15130F] px-8 text-base font-semibold text-white shadow-[0_8px_18px_rgba(0,0,0,0.16)] transition hover:bg-[#1F2A22]"
            >
              เริ่มใช้งาน
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

function McpLogo({ src, className = "" }) {
  return (
    <div className={`grid min-h-[132px] place-items-center ${className}`}>
      <div className="grid h-32 w-32 place-items-center rounded-[34px] bg-white shadow-[0_24px_70px_rgba(22,29,24,0.12)]">
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="h-20 w-20 object-contain"
        />
      </div>
    </div>
  );
}

export default McpPage;
